import axios from 'axios'
import queryString from 'query-string'
import { CreditCardStatus } from 'src/models/creditCard.models'
import { CreditCardPermissions } from 'src/models/creditCardPermissions.models'
import { CreditCardsResponse } from 'src/queries/creditCard.queries'
import { CreditCardVariant } from 'src/queries/queries.constants'
import { getRequestConfig } from 'src/queries/requestConfig'
import { GetCreditCardsRequest } from 'src/requests/getCreditCards.request'

// Base params shared by all variants
const baseParams: Partial<GetCreditCardsRequest> = {
  status: CreditCardStatus.Active,
  withPermission: CreditCardPermissions.IssueVirtualCards,
  sortField: 'type',
  sortDirection: 'ASC',
  count: 10,
}

// Variant-specific filters matching the existing Redux thunks exactly.
// `billPay` filters by billPayEnabled. Whether it ALSO pins
// balanceLockedEnabled:false is decided per-request by the
// enable_declining_balance_bill_pay flag (see fetchCreditCardsForForm): un-pin
// only when the flag is on, so balance-locked (FIS/Sunflower declining-balance)
// funding sources appear in the bill-pay dropdown.
const variantFilters: Record<
  CreditCardVariant,
  Partial<GetCreditCardsRequest>
> = {
  standard: { balanceLockedEnabled: false },
  billPay: { billPayEnabled: true },
  recurring: { recurrenceEnabled: true },
  guestAccess: { directEnabled: true },
  balanceLock: { balanceLockedEnabled: true },
  issuerCycle: { issuerCycleEnabled: true },
}

export const fetchCreditCardsForForm = async (
  variant: CreditCardVariant,
  page: number = 0,
  keyword?: string,
  decliningBalanceBillPayEnabled: boolean = false,
): Promise<CreditCardsResponse> => {
  const requestConfig = getRequestConfig()

  // Un-pin balanceLockedEnabled for bill pay only when the declining-balance
  // flag is on; otherwise keep the historical standard-pool pin so
  // balance-locked sources stay hidden.
  const variantFilter =
    variant === 'billPay' && !decliningBalanceBillPayEnabled
      ? { ...variantFilters.billPay, balanceLockedEnabled: false }
      : variantFilters[variant]

  const params: GetCreditCardsRequest = {
    ...baseParams,
    ...variantFilter,
    page,
    ...(keyword ? { search: keyword } : {}),
  }

  const response = await axios.get<CreditCardsResponse>(
    `creditcards?${queryString.stringify(params)}`,
    requestConfig,
  )
  return response.data
}
